/**
 * Three-tier pricing page (Free / Pro / Agency).
 *
 * Fixes legacy P0 #2: the legacy `Pricing` component called `activate-trial`
 * for ALL plan CTAs, which meant nobody ever paid. Here we route correctly:
 *
 *   - "Start free" (always free tier) → no API call, just send to /auth?mode=sign-up
 *   - "Start free trial" (Pro / Agency, 14-day no-card) → /activate-trial
 *   - "Subscribe" (Pro / Agency, paid)                  → /create-checkout
 *
 * Visitors who aren't signed in are routed to /auth?mode=sign-up first;
 * the redirect carries the selected plan so we can resume the action
 * post-signup (handled in /auth via location.state).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { Check, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useMe } from '@/hooks/useMe';
import { api, ApiHttpError } from '@/lib/api';
import { analytics } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';
import { cn } from '@/lib/utils';

type PaidPlan = 'entry' | 'pro' | 'agency';
type Action = 'free' | 'trial' | 'subscribe';

interface Tier {
  id: 'free' | PaidPlan;
  name: string;
  monthly: string;
  blurb: string;
  highlights: string[];
  cta: Action;
  highlight?: boolean;
}

// Highlights are kept in sync with PLAN_LIMITS in api/src/lib/entitlements.ts.
// If you change one, change the other. Lying here to a paying customer is the
// fastest way to a refund — only list what the backend actually enforces.
const TIERS: Tier[] = [
  {
    id: 'free',
    name: 'Free',
    monthly: '$0',
    blurb: 'One domain, get a baseline, see what to fix.',
    highlights: [
      '1 domain',
      'On-demand SPF / DKIM / DMARC / PTR scan',
      'MTA-STS + TLS-RPT checks',
      'Top 3 IP blocklists',
      '20 scans / hour',
    ],
    cta: 'free',
  },
  {
    id: 'entry',
    name: 'Entry',
    monthly: '$12',
    blurb: 'For prosumers. Multiple domains, full blocklist coverage, alerts.',
    highlights: [
      '5 domains',
      'Full 11 blocklist coverage (Spamhaus, SURBL, URIBL…)',
      'DMARC RUA aggregate ingest',
      'Google Postmaster Tools sync',
      'Email + Slack alerts on score drops',
      '200 scans / hour',
    ],
    cta: 'trial',
  },
  {
    id: 'pro',
    name: 'Pro',
    monthly: '$39',
    blurb: 'For serious senders. Unlimited scans, public reports, API.',
    highlights: [
      '25 domains',
      'Unlimited scans',
      'Everything in Entry',
      'Public shareable report URL',
      'Continuous change-triggered alerts',
      '1 000 scans / hour',
    ],
    cta: 'trial',
    highlight: true,
  },
  {
    id: 'agency',
    name: 'Agency',
    monthly: '$119',
    blurb: 'For deliverability consultancies. Multi-client portfolios.',
    highlights: [
      '100 domains',
      'Everything in Pro',
      'Multi-client portfolio dashboard',
      'Branded PDF reports',
      '5 000 scans / hour',
    ],
    cta: 'trial',
  },
];

export function PricingPage() {
  return (
    <>
      <Helmet>
        <title>Pricing — InboxGuard</title>
        <meta name="description" content="Pricing for InboxGuard. Free, Pro, and Agency tiers with continuous email deliverability monitoring." />
        <link rel="canonical" href="https://inboxguard.io/pricing" />
        <meta property="og:title" content="Pricing — InboxGuard" />
        <meta property="og:url" content="https://inboxguard.io/pricing" />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <PublicHeader />

        <section className="hero-grad">
          <main className="container mx-auto px-4 py-16 sm:py-20">
            <div className="mx-auto max-w-2xl text-center animate-fade-in-up">
              <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                14-day no-card trial on every paid plan
              </span>
              <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl">
                Simple pricing.
                <span className="bg-gradient-to-r from-primary to-emerald-400 bg-clip-text text-transparent">
                  {' '}No deliverability tax.
                </span>
              </h1>
              <p className="mx-auto mt-4 max-w-xl text-balance text-lg text-muted-foreground">
                Start free. Add your domain. Upgrade when you're ready for continuous monitoring,
                alerting, and the full check stack.
              </p>
            </div>

            <div className="mx-auto mt-12 grid grid-cols-1 max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {TIERS.map((tier) => (
                <TierCard key={tier.id} tier={tier} />
              ))}
            </div>

            <p className="mx-auto mt-12 max-w-2xl text-center text-sm text-muted-foreground">
              All paid plans include a 14-day no-card trial. Cancel any time.
            </p>
          </main>
        </section>
        <PublicFooter />
      </div>
    </>
  );
}

function TierCard({ tier }: { tier: Tier }) {
  const auth = useAuth();
  const me = useMe();
  const nav = useNavigate();
  const [pendingAction, setPendingAction] = useState<Action | null>(null);
  const currentPlan = me.data?.plan?.tier ?? null;
  const isOnPaidPlan = currentPlan != null && currentPlan !== 'free' && me.data?.plan?.trialing === false;

  const startTrial = useMutation<unknown, ApiHttpError, PaidPlan>({
    mutationFn: (plan) => api.post('/activate-trial', { plan }),
    onSuccess: (_data, plan) => {
      // Fires both `purchase` (GA4 recommended) and `plan_upgraded` (legacy).
      analytics.trackPurchase({ plan, kind: 'trial' });
      toast.success(`Your ${tier.name} trial is active for 14 days.`);
      nav('/dashboard');
    },
    onError: (err) => toast.error(err.message),
  });

  function handleCta(action: Action) {
    setPendingAction(action);
    // CTA-level funnel signal — lets us see drop-off at each pricing column
    // (entry vs pro vs agency) and which CTA flavor (free / trial / subscribe).
    analytics.trackCtaClick(`pricing_${tier.id}_${action}`, 'pricing', { plan: tier.id, action });

    // Route unauthenticated visitors to sign-up first; carry the action so
    // we can resume after signup (handled by the auth flow's post-confirm hook).
    if (!auth.user) {
      const params = new URLSearchParams({ mode: 'sign-up' });
      if (tier.id !== 'free') params.set('plan', tier.id);
      if (action !== 'free') params.set('action', action);
      nav(`/auth?${params.toString()}`);
      return;
    }

    if (action === 'free') {
      nav('/dashboard');
      return;
    }
    if (action === 'trial') {
      if (tier.id === 'free') return;
      startTrial.mutate(tier.id);
      return;
    }
    if (action === 'subscribe') {
      if (tier.id === 'free') return;
      // Existing paid subscribers: route to /settings/billing → ChangePlanModal
      // pre-loaded for this tier. Avoids creating a duplicate subscription on
      // the same Stripe customer.
      if (isOnPaidPlan && tier.id !== currentPlan) {
        nav(`/settings/billing?changeTo=${tier.id}`);
        return;
      }
      if (isOnPaidPlan && tier.id === currentPlan) {
        nav('/settings/billing');
        return;
      }
      // First-time subscribers: native PaymentElement checkout.
      nav(`/billing/checkout?plan=${tier.id}`);
      return;
    }
  }

  const isWorking = pendingAction === 'trial' && startTrial.isPending;

  return (
    <Card
      className={cn(
        'card-elev relative flex flex-col',
        tier.highlight && 'border-primary shadow-md ring-1 ring-primary/30',
      )}
    >
      <CardHeader>
        {tier.highlight && (
          <span className="mb-2 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-primary">
            Most popular
          </span>
        )}
        <CardTitle>{tier.name}</CardTitle>
        <CardDescription>{tier.blurb}</CardDescription>
        <div className="mt-4 flex items-baseline gap-1">
          <span className="text-4xl font-bold">{tier.monthly}</span>
          {tier.id !== 'free' && <span className="text-sm text-muted-foreground">/mo</span>}
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <ul className="space-y-2 text-sm">
          {tier.highlights.map((h) => (
            <li key={h} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>{h}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 space-y-2">
          {tier.id === 'free' && (
            <Button className="w-full" variant="outline" onClick={() => handleCta('free')}>
              Start free
            </Button>
          )}
          {tier.id !== 'free' && (
            <>
              <Button
                className="w-full"
                onClick={() => handleCta('trial')}
                disabled={isWorking}
              >
                {isWorking && pendingAction === 'trial' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Start 14-day trial'
                )}
              </Button>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => handleCta('subscribe')}
                disabled={isWorking}
              >
                Subscribe now
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default PricingPage;
